import { Subscribable, Subscription } from '@benzinga/subscribable';
import { Holding } from './entities';
import { Session } from '@benzinga/session';
import { HoldingsRequest } from './request';
import { oneRequestAtATime } from '@benzinga/utils';
import { HoldingsStore } from './store';

export interface HoldingsGetRequestEvent {
  indexHoldings: Holding[];
  type: 'holdings:get_holdings';
}

export interface HoldingsErrorEvent {
  error: Error | string;
  type: 'holdings:error_holdings';
}

export interface HoldingsUpdateEvent {
  type: 'holdings:update_holdings';
  indexHoldings: Holding[];
}

export type HoldingsRequestEvent = HoldingsGetRequestEvent | HoldingsErrorEvent | HoldingsUpdateEvent;

export class HoldingsManager extends Subscribable<HoldingsRequestEvent> {
  private request: HoldingsRequest;
  private store = new HoldingsStore();
  private storeSubscription?: Subscription<HoldingsStore>;
  private requestSubscription?: Subscription<HoldingsRequest>;
  private refreshInterval?: ReturnType<typeof setInterval>;
  private fetchHolding = oneRequestAtATime(async () => {
    const indexHoldings = await this.request.getHoldings();
    if (indexHoldings.err) {
      return indexHoldings.err;
    } else {
      return this.store.updateHoldings(indexHoldings.ok ?? []);
    }
  });
  constructor(session: Session) {
    super();
    this.request = new HoldingsRequest(session);
    this.fetchHolding();
    session.onPostSessionClose(() => {
      this.clearRefreshInterval();
      this.requestSubscription?.unsubscribe();
    });
  }

  private clearRefreshInterval = (): void => {
    if (this.refreshInterval !== undefined) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = undefined;
    }
  };

  public static getName = () => 'benzinga-holdings';

  public getHoldingsCached = (): Holding[] => this.store.getIndexStoreHoldings();

  public getCachedHoldingByName = (indexName: string): Holding | undefined =>
    this.store.getCachedHoldingByIndexName(indexName);

  protected onFirstSubscription(): void {
    this.requestSubscription = this.request.subscribe(event => this.dispatch(event));
    this.storeSubscription = this.store.subscribe(event => this.dispatch(event));
    if (this.refreshInterval === undefined) {
      this.refreshInterval = setInterval(() => this.fetchHolding(), 1000 * 60 * 60 * 24); //24 hr interval for fetching holdings
    }
  }

  protected onZeroSubscriptions(): void {
    this.clearRefreshInterval();
    this.requestSubscription?.unsubscribe();
    this.storeSubscription?.unsubscribe();
  }
}
